import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatDateTime, prettify } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Workflow, Zap, Trash2, History, Play, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", description: "", trigger_type: "", trigger_config: {}, actions: [], is_active: true };

export default function Automations() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [runs, setRuns] = useState(null);
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([api.get("/marketing/automations"), api.get("/marketing/automations/meta")]);
      setItems(a.data.items || []); setMeta(m.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const triggerLabel = (v) => meta?.triggers?.find((t) => t.value === v)?.label || prettify(v || "");
  const triggerDef = (v) => meta?.triggers?.find((t) => t.value === v);
  const actionDef = (v) => meta?.actions?.find((a) => a.value === v);

  const save = async () => {
    if (!editor.name.trim()) return toast.error("Name is required");
    if (!editor.trigger_type) return toast.error("Pick a trigger");
    try {
      const payload = { name: editor.name, description: editor.description, trigger_type: editor.trigger_type, trigger_config: editor.trigger_config, actions: editor.actions, is_active: editor.is_active };
      if (editor.id) await api.put(`/marketing/automations/${editor.id}`, payload);
      else await api.post("/marketing/automations", payload);
      toast.success("Automation saved"); setEditor(null); load();
    } catch { toast.error("Could not save automation"); }
  };
  const toggle = async (a) => { await api.post(`/marketing/automations/${a.id}/toggle`); load(); };
  const remove = async (a) => { await api.delete(`/marketing/automations/${a.id}`); toast.success("Deleted"); load(); };
  const openRuns = async (a) => {
    try { const { data } = await api.get(`/marketing/automations/${a.id}/runs`); setRuns({ auto: a, items: data.items || [] }); }
    catch { toast.error("Could not load runs"); }
  };
  const processDue = async () => {
    setProcessing(true);
    try { const { data } = await api.post("/marketing/automations/process-due"); toast.success(`Processed ${data.processed} scheduled action(s)`); }
    catch { toast.error("Could not process"); } finally { setProcessing(false); }
  };

  // action editing
  const addAction = () => setEditor((e) => ({ ...e, actions: [...(e.actions || []), { type: meta.actions[0].value, config: {} }] }));
  const updateAction = (i, patch) => setEditor((e) => { const acts = [...e.actions]; acts[i] = { ...acts[i], ...patch }; return { ...e, actions: acts }; });
  const updateActionConfig = (i, key, val) => setEditor((e) => { const acts = [...e.actions]; acts[i] = { ...acts[i], config: { ...acts[i].config, [key]: val } }; return { ...e, actions: acts }; });
  const removeAction = (i) => setEditor((e) => ({ ...e, actions: e.actions.filter((_, idx) => idx !== i) }));

  const renderField = (field, value, onChange, testid) => {
    if (field.type === "number") return <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testid} />;
    if (field.type === "textarea") return <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
    if (field.type === "priority" || field.type === "lead_status" || field.type === "case_status") {
      const opts = field.type === "priority" ? meta.priorities : field.type === "lead_status" ? meta.lead_statuses : meta.case_statuses;
      return (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{(opts || []).map((o) => <SelectItem key={o} value={o}>{prettify(o)}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
  };

  return (
    <div className="space-y-5" data-testid="marketing-automations">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#252221]">Automations</h1>
          <p className="text-sm text-muted-foreground">Trigger follow-up tasks, assignments, notifications &amp; nurture sequences automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={processDue} disabled={processing} data-testid="process-due-button">{processing ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Play size={15} className="mr-1.5" />} Run scheduled</Button>
          <Button onClick={() => setEditor({ ...blank, actions: [] })} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="new-automation-button"><Plus size={15} className="mr-1.5" /> New automation</Button>
        </div>
      </div>

      {loading ? <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div> :
        items.length === 0 ? (
          <Card className="p-12 text-center border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="automations-empty">
            <Workflow size={26} className="mx-auto text-[#1C4358] mb-3" />
            <p className="font-medium text-[#252221]">No automations yet</p>
            <p className="text-sm text-muted-foreground">Create a rule, e.g. “When a new lead arrives, create a follow-up task.”</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((a) => (
              <Card key={a.id} className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="automation-card">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(107,70,193,0.12)" }}><Zap size={18} className="text-[#6B46C1]" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[#252221]">{a.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">When: <span className="font-medium text-[#1C4358]">{triggerLabel(a.trigger_type)}</span> · {(a.actions || []).length} action(s) · {a.run_count || 0} runs</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch checked={a.is_active} onCheckedChange={() => toggle(a)} data-testid="automation-toggle" />
                    <Button size="sm" variant="outline" onClick={() => openRuns(a)} data-testid="automation-runs-button"><History size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => setEditor({ ...blank, ...a, trigger_config: a.trigger_config || {}, actions: a.actions || [] })} data-testid="automation-edit-button">Edit</Button>
                    <button onClick={() => remove(a)} className="p-2 rounded-lg text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]" data-testid="automation-delete" aria-label="Delete"><Trash2 size={15} /></button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

      {/* Builder */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="sm:max-w-xl max-h-[88vh] overflow-y-auto mmm-scroll" data-testid="automation-builder">
          <DialogHeader><DialogTitle>{editor?.id ? "Edit automation" : "New automation"}</DialogTitle></DialogHeader>
          {editor && meta && (
            <div className="space-y-4">
              <div><Label className="text-sm">Name</Label><Input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} placeholder="New web lead welcome" data-testid="automation-name" /></div>
              <div className="rounded-xl border border-[#E6EDF2] p-3">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">When this happens</Label>
                <Select value={editor.trigger_type || ""} onValueChange={(v) => setEditor({ ...editor, trigger_type: v, trigger_config: {} })}>
                  <SelectTrigger className="mt-1" data-testid="automation-trigger"><SelectValue placeholder="Choose a trigger" /></SelectTrigger>
                  <SelectContent>{meta.triggers.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                {(triggerDef(editor.trigger_type)?.config || []).map((f) => (
                  <div key={f.key} className="mt-2"><Label className="text-xs">{f.label}</Label>{renderField(f, editor.trigger_config?.[f.key], (val) => setEditor({ ...editor, trigger_config: { ...editor.trigger_config, [f.key]: val } }), `trigger-cfg-${f.key}`)}</div>
                ))}
              </div>

              <div className="rounded-xl border border-[#E6EDF2] p-3 space-y-3">
                <div className="flex items-center justify-between"><Label className="text-xs uppercase tracking-wide text-muted-foreground">Do this</Label><Button size="sm" variant="outline" onClick={addAction} data-testid="add-action-button"><Plus size={13} className="mr-1" /> Add action</Button></div>
                {(editor.actions || []).length === 0 && <p className="text-sm text-muted-foreground">No actions yet. Add at least one.</p>}
                {(editor.actions || []).map((act, i) => (
                  <div key={i} className="rounded-lg bg-[#F7FAFC] p-3 space-y-2" data-testid="action-row">
                    <div className="flex items-center gap-2">
                      <Select value={act.type} onValueChange={(v) => updateAction(i, { type: v, config: {} })}>
                        <SelectTrigger className="flex-1" data-testid="action-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{meta.actions.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <button onClick={() => removeAction(i)} className="p-2 rounded-lg text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]" data-testid="action-remove" aria-label="Remove action"><X size={15} /></button>
                    </div>
                    {(actionDef(act.type)?.config || []).map((f) => (
                      <div key={f.key}><Label className="text-xs">{f.label}</Label>{renderField(f, act.config?.[f.key], (val) => updateActionConfig(i, f.key, val), `action-${i}-${f.key}`)}</div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between rounded-lg border border-[#E6EDF2] p-3"><div className="text-sm font-medium">Active</div><Switch checked={editor.is_active} onCheckedChange={(v) => setEditor({ ...editor, is_active: v })} data-testid="automation-active" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditor(null)}>Cancel</Button><Button onClick={save} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="automation-save">Save automation</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Runs */}
      <Dialog open={!!runs} onOpenChange={(o) => !o && setRuns(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto mmm-scroll" data-testid="automation-runs-dialog">
          <DialogHeader><DialogTitle>Run history · {runs?.auto?.name}</DialogTitle></DialogHeader>
          {runs?.items?.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No runs yet. This automation will log here when it fires.</p> : (
            <div className="space-y-2">
              {(runs?.items || []).map((r) => (
                <div key={r.id} className="rounded-lg border border-[#E6EDF2] p-3" data-testid="automation-run-row">
                  <div className="flex items-center justify-between"><span className="text-sm font-medium text-[#252221]">{prettify(r.trigger_type)}</span><span className="text-xs text-muted-foreground">{formatDateTime(r.created_at)}</span></div>
                  <div className="text-xs text-muted-foreground mt-1">{(r.actions_summary || []).map((a) => a.action).join(", ")}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
